'use client'

import { FC, FormEvent } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import * as Yup from 'yup'

import { Button, Checkbox } from '@/components/commons'
import { yupResolver } from '@hookform/resolvers/yup'

import { MethodEnum, StepEnum } from '../ForgotPassword/types'
import { IContactFormProps } from './types'

const ContactForm: FC<IContactFormProps> = ({
  contacts,
  handleSendCodeReset,
  sendButtonLoading,
  setStep,
  setMethod,
  onCancel,
}) => {
  const schema = Yup.object({
    contact: Yup.string().required('Você deve selecionar uma opção'),
  })

  type FormProps = Yup.InferType<typeof schema>

  const defaultValues: FormProps = {
    contact: '',
  }

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { isValid },
  } = useForm<FormProps>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues,
  })

  const onSubmit: SubmitHandler<FormProps> = async (data) => {
    const [tenantId, methodType] = data.contact.split('|')
    const method = methodType === 'email' ? MethodEnum.EMAIL : MethodEnum.SMS

    setMethod(method)

    await handleSendCodeReset({
      method,
      document: contacts.listForResetByTenant[0]?.document || '',
      tenantId,
    })
  }

  function handleCheckContact(e: FormEvent<HTMLInputElement>) {
    if (e.currentTarget.value) {
      setValue('contact', e.currentTarget.value)
      trigger('contact')
    }

    return e.currentTarget.value
  }

  return (
    <form
      className="flex flex-col w-full bg-white gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col gap-2">
        {contacts.notification && (
          <p className="prose-body text-primary-900">{contacts.notification}</p>
        )}

        <div className="flex flex-col gap-2 w-full">
          {contacts?.listForResetByTenant?.map((contact, index) => (
            <div
              key={`${contact.tenantId}-${index}`}
              className="flex flex-col gap-2"
            >
              <p className="prose-body-small text-primary-900 font-semibold">
                {contact.tenantName}
              </p>
              <div className="flex flex-col gap-2 pl-4">
                <Checkbox
                  label={`Email: ${contact.email}`}
                  typeBox="radio"
                  id={`email-${contact.tenantId}-${index}`}
                  value={`${contact.tenantId}|email`}
                  checked={watch('contact') === `${contact.tenantId}|email`}
                  {...register('contact', {
                    onChange: handleCheckContact,
                    onBlur: handleCheckContact,
                  })}
                />
                <Checkbox
                  label={`Celular: ${contact.cellphone}`}
                  typeBox="radio"
                  id={`phone-${contact.tenantId}-${index}`}
                  value={`${contact.tenantId}|phone`}
                  checked={watch('contact') === `${contact.tenantId}|phone`}
                  {...register('contact', {
                    onChange: handleCheckContact,
                    onBlur: handleCheckContact,
                  })}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        type="submit"
        text="Enviar código"
        disabled={!isValid || sendButtonLoading}
        isLoading={sendButtonLoading}
      />
      <div className="flex w-full justify-between items-center">
        {onCancel && (
          <p
            onClick={onCancel}
            className="prose-body-small text-grayscale-600 hover:underline cursor-pointer"
          >
            Cancelar
          </p>
        )}
        <p
          onClick={() => setStep(StepEnum.CONFIRM_RESET_WITH_CODE)}
          className={`prose-body-small text-grayscale-600 hover:underline cursor-pointer ${
            onCancel ? '' : 'self-end'
          }`}
        >
          Já tem um código? Clique aqui.
        </p>
      </div>
    </form>
  )
}

export default ContactForm
